import React, { memo, useCallback, useEffect, useState } from "react";
import {
  ColorService,
  IColorRgb,
  IColorHsv,
  IColor,
} from "react-color-palette";
import Input from "../../atoms/inputs/Input";
import Button from "../button/Button";

export function float(value: number, decimalPlaces: number) {
  return Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

export function formatRgb({ r, g, b, a }: IColor["rgb"]) {
  const rgb: any[] = [Math.round(r), Math.round(g), Math.round(b)];
  const alpha = float(a, 3);

  if (alpha < 1) rgb.push(alpha);

  return rgb.join(", ");
}

export function formatHsv({ h, s, v, a }: IColor["hsv"]) {
  const hsv: any[] = [
    `${Math.round(h)}°`,
    `${Math.round(s)}%`,
    `${Math.round(v)}%`,
  ];
  const alpha = float(a, 3);

  if (alpha < 1) hsv.push(alpha);

  return hsv.join(", ");
}

interface IFieldsProps {
  readonly hideInput: (keyof IColor)[] | boolean;
  readonly color: IColor;
  readonly onChange: (color: IColor) => void;
  readonly handleSave: (color: IColor) => void;
}

export const ColorPickerFields = memo(
  ({ hideInput, color, onChange, handleSave }: IFieldsProps) => {
    const [fields, setFields] = useState({
      hex: {
        value: color.hex,
        inputted: false,
      },
      rgb: {
        value: formatRgb(color.rgb),
        inputted: false,
      },
      hsv: {
        value: formatHsv(color.hsv),
        inputted: false,
      },
    });

    useEffect(() => {
      if (!fields.hex.inputted) {
        setFields((fields) => ({
          ...fields,
          hex: { ...fields.hex, value: color.hex },
        }));
      }
    }, [fields.hex.inputted, color.hex]);

    useEffect(() => {
      if (!fields.rgb.inputted) {
        setFields((fields) => ({
          ...fields,
          rgb: { ...fields.rgb, value: formatRgb(color.rgb) },
        }));
      }
    }, [fields.rgb.inputted, color.rgb]);

    useEffect(() => {
      if (!fields.hsv.inputted) {
        setFields((fields) => ({
          ...fields,
          hsv: { ...fields.hsv, value: formatHsv(color.hsv) },
        }));
      }
    }, [fields.hsv.inputted, color.hsv]);

    const onInputChange = useCallback(
      <T extends keyof typeof fields>(field: T) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = event.target;

          setFields((fields) => ({
            ...fields,
            [field]: { ...fields[field], value },
          }));

          if (field === "hsv")
            onChange(ColorService.convert("hsv", ColorService.toHsv(value)));
          else if (field === "rgb")
            onChange(ColorService.convert("rgb", ColorService.toRgb(value)));
          else onChange(ColorService.convert("hex", value));
        },
      [onChange]
    );

    const onInputFocus = useCallback(
      <T extends keyof typeof fields>(field: T) =>
        () => {
          setFields((fields) => ({
            ...fields,
            [field]: { ...fields[field], inputted: true },
          }));
        },
      []
    );

    const onInputBlur = useCallback(
      <T extends keyof typeof fields>(field: T) =>
        () => {
          setFields((fields) => ({
            ...fields,
            [field]: { ...fields[field], inputted: false },
          }));
        },
      []
    );

    {
      /* <Input
          label="RGB"
          id="rgb"
          className="rcp-field-input"
          value={fields.rgb.value}
          onChange={onInputChange("rgb")}
          onFocus={onInputFocus("rgb")}
          onBlur={onInputBlur("rgb")}
        /> */
    }

    return (
      <div className="flex w-full items-end justify-start gap-8">
        <Input
          label="HEX"
          id="hex"
          className="max-w-[120px] "
          value={fields.hex.value}
          onChange={onInputChange("hex")}
          onFocus={onInputFocus("hex")}
          onBlur={onInputBlur("hex")}
        />
        <Button
          variant="primary"
          id="colorPickerSubmit"
          leadingIcon="checkmark-md"
          // text={t("util:save", { val: t("global:color") })}
          onClick={handleSave}
          classNameWrapper="ml-auto"
        />
      </div>
    );
  }
);
